'use client'

import { useState, useEffect } from 'react'
import Image from 'next/image'
import Link from 'next/link'
import { usePathname } from 'next/navigation'

import logoIcon from '@images/navbar/logo_main-nav.svg'
import NavigateToContact from './NavigateToContact'

import styles from '@app/v1/v1.module.scss'

export default function InvestorsHeader(): JSX.Element {
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [isScrolled, setIsScrolled] = useState(false)
  const pathname = usePathname()
  const isInvestorsPage = pathname?.includes('/investors')

  useEffect(() => {
    const handleScroll = (): void => {
      const currentScrollY = window.scrollY
      setIsScrolled(currentScrollY > 50)
    }

    window.addEventListener('scroll', handleScroll, { passive: true })
    return (): void => window.removeEventListener('scroll', handleScroll)
  }, [])

  useEffect(() => {
    // Add class to body when mobile menu is open to push content down
    if (isMenuOpen) {
      document.body.classList.add('mobileMenuOpen')
    } else {
      document.body.classList.remove('mobileMenuOpen')
    }
    return (): void => {
      document.body.classList.remove('mobileMenuOpen')
    }
  }, [isMenuOpen])

  useEffect(() => {
    // Close mobile menu when screen is resized to desktop size (md breakpoint = 768px)
    const handleResize = (): void => {
      if (window.innerWidth >= 768 && isMenuOpen) {
        setIsMenuOpen(false)
      }
    }

    window.addEventListener('resize', handleResize)
    return (): void => window.removeEventListener('resize', handleResize)
  }, [isMenuOpen])

  return (
    <header className={`${styles.header} ${isScrolled ? styles.headerScrolled : ''}`}>
      <div className={styles.headerInner}>
        <Link href="/" className={styles.logoContainer}>
          <Image src={logoIcon} alt="SonicJobs" width={131} height={33} className={styles.logoImage} />
        </Link>

        <nav className={styles.nav}>
          <Link href="/" className={styles.navLink}>
            Home
          </Link>
          <Link href="/jobs" className={styles.navLink}>
            Browse Jobs
          </Link>
          <Link href="/investors" className={`${styles.navLink} ${isInvestorsPage ? styles.navLinkActive : ''}`}>
            Computer Use Agents
          </Link>
          <Link href="/chatgpt-integration" className={styles.mobileNavLink}>
            ChatGPT Integration
          </Link>
        </nav>

        <NavigateToContact className={styles.buttonGetStarted}>Contact Us</NavigateToContact>

        <button
          className={styles.menuButton}
          type="button"
          aria-label="Toggle menu"
          aria-expanded={isMenuOpen}
          onClick={() => setIsMenuOpen((open) => !open)}
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path
              d="M4 7H20M4 12H20M4 17H20"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      </div>

      <div className={`${styles.mobileNav} ${isMenuOpen ? styles.mobileNavOpen : ''}`}>
        <nav className={styles.mobileNavList}>
          <Link href="/" className={styles.mobileNavLink} onClick={() => setIsMenuOpen(false)}>
            Home
          </Link>
          <Link href="/jobs" className={styles.mobileNavLink} onClick={() => setIsMenuOpen(false)}>
            Browse Jobs
          </Link>
          <Link
            href="/investors"
            className={`${styles.mobileNavLink} ${isInvestorsPage ? styles.mobileNavLinkActive : ''}`}
            onClick={() => setIsMenuOpen(false)}
          >
            Computer Use Agents
          </Link>
          <Link href="/chatgpt-integration" className={styles.mobileNavLink}>
            ChatGPT Integration
          </Link>
          <NavigateToContact className={styles.mobileButtonGetStarted} onClick={() => setIsMenuOpen(false)}>
            Contact Us
          </NavigateToContact>
        </nav>
      </div>
    </header>
  )
}
